import React from 'react';
import { useStore } from '../../../store';
import { ValidatedAIResponse } from '../../../ai/responseSchema';
import { buildSymbolContextForAI } from '../../../services/buildSymbolContext';
import { assessPositionSize, SIZING_TIER_LABELS, SIZING_TIER_COLORS } from '../../../risk/positionSizing';

interface Props {
  thesis: ValidatedAIResponse;
  ctx: ReturnType<typeof buildSymbolContextForAI>;
}

export function SizingCard({ thesis, ctx }: Props) {
  const { settings } = useStore();
  if (!ctx) return null;
  const firstSetup = ctx.setups?.[0] ?? null;
  if (!firstSetup) return null;

  const sizing = assessPositionSize(
    firstSetup.entryPrice ?? null,
    firstSetup.stopPrice  ?? null,
    firstSetup.target1Price ?? null,
    firstSetup.confidence >= 70 ? 'ready' : firstSetup.confidence >= 55 ? 'good' : 'interesting',
    firstSetup.confidence,
    thesis.conviction,
    'balanced', 0, 0, 0,
    {
      accountSize:         settings.accountSize ?? null,
      riskPerTradePct:     settings.riskPerTradePct ?? 1.0,
      maxConcurrentTrades: settings.maxConcurrentTrades ?? 5,
      maxSameThemePct:     30,
      maxSameDirectionPct: 60,
    }
  );

  const tierColor = SIZING_TIER_COLORS[sizing.tier];
  const tierLabel = SIZING_TIER_LABELS[sizing.tier];

  return (
    <div style={{ marginTop: 12, padding: '8px 12px',
      background: `${tierColor}08`, border: `1px solid ${tierColor}33`, borderRadius: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 9, fontWeight: 700, fontFamily: 'var(--mono)', color: tierColor, letterSpacing: '1px' }}>
          SIZING POSTURE
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)', color: tierColor,
          border: `1px solid ${tierColor}55`, padding: '1px 8px', borderRadius: 4 }}>
          {tierLabel}
        </span>
        {sizing.expectedR !== null && (
          <span style={{ fontSize: 9, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', marginLeft: 'auto' }}>
            R/R: {sizing.expectedR.toFixed(1)}
          </span>
        )}
      </div>
      <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: sizing.constraintNotes.length ? 4 : 0 }}>
        {sizing.tierReason}
      </div>
      {sizing.constraintNotes.length > 0 && (
        <div style={{ fontSize: 9, color: 'var(--amber)', fontFamily: 'var(--mono)', marginTop: 3 }}>
          {sizing.constraintNotes.join(' · ')}
        </div>
      )}
      {(sizing.dollarRiskTarget !== null || sizing.normalizedRiskUnits > 0) && (
        <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
          {sizing.dollarRiskTarget !== null ? (
            <>
              <div>
                <div style={{ fontSize: 8, color: 'var(--t3)', fontFamily: 'var(--mono)' }}>DOLLAR RISK</div>
                <div style={{ fontSize: 12, color: tierColor, fontWeight: 700, fontFamily: 'var(--mono)' }}>
                  ${sizing.dollarRiskTarget.toLocaleString()}
                </div>
              </div>
              {sizing.suggestedShares !== null && (
                <div>
                  <div style={{ fontSize: 8, color: 'var(--t3)', fontFamily: 'var(--mono)' }}>SHARES</div>
                  <div style={{ fontSize: 12, color: tierColor, fontWeight: 700, fontFamily: 'var(--mono)' }}>
                    {sizing.suggestedShares.toLocaleString()}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div>
              <div style={{ fontSize: 8, color: 'var(--t3)', fontFamily: 'var(--mono)' }}>NORMALIZED RISK</div>
              <div style={{ fontSize: 12, color: tierColor, fontWeight: 700, fontFamily: 'var(--mono)' }}>
                {sizing.normalizedRiskUnits.toFixed(2)}× unit
              </div>
            </div>
          )}
        </div>
      )}
      <div style={{ fontSize: 8, color: 'var(--t3)', marginTop: 6, fontStyle: 'italic', lineHeight: 1.4 }}>
        {sizing.disclosureNote}
      </div>
    </div>
  );
}
